import { i18n } from '@lingui/core'
import { Fragment } from 'react/jsx-runtime'
import styled, { useTheme } from 'styled-components'

import { FlexColumn, Padding, Spacing } from '@/@bothrs-ui/web/components/layout'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { Loader } from '@/modules/shared/components/loader/Loader'
import { ScreenType, useScreenType } from '@/modules/shared/hooks/useScreenType'
import { DocumentFilter } from '../document-filter/DocumentFilter'
import { DocumentLink } from '../document-link/DocumentLink'
import { DocumentsEmptySection } from './documents-empty-section/DocumentsEmptySection'

import type {
  PatientDocumentsFilterOption,
  PatientViewDocument,
  PatientViewDocumentsByYear,
} from '../../types/patient-documents.types'

type Props = {
  selectedDocumentId?: string
  incomingFilesByYear: PatientViewDocumentsByYear
  isLoading?: boolean
  className?: string
  selectedFilter: PatientDocumentsFilterOption
  onFilterChange: (filter: PatientDocumentsFilterOption) => void
  onDocumentClick: (documentId: string) => void
  filterMetadataMap?: Map<PatientDocumentsFilterOption, number>
}

export const DocumentList = ({
  selectedDocumentId,
  incomingFilesByYear,
  isLoading,
  className,
  selectedFilter,
  onFilterChange,
  onDocumentClick,
  filterMetadataMap,
}: Props) => {
  const filters: {
    id: PatientDocumentsFilterOption
    name: string
    unread?: number
  }[] = [
    {
      id: 'all',
      name: i18n._(globalDefinitions.document.filters.all),
      unread: 0,
    },
    {
      id: 'brochures',
      name: i18n._(globalDefinitions.document.filters.brochures),
      unread: filterMetadataMap?.get('brochures') ?? 0,
    },
    {
      id: 'documents',
      name: i18n._(globalDefinitions.document.filters.documents),
      unread: filterMetadataMap?.get('documents') ?? 0,
    },
    {
      id: 'toSign',
      name: i18n._(globalDefinitions.document.filters.toSign),
      unread: filterMetadataMap?.get('toSign') ?? 0,
    },
    {
      id: 'signed',
      name: i18n._(globalDefinitions.document.filters.signed),
      unread: filterMetadataMap?.get('signed') ?? 0,
    },
  ]

  return (
    <StContainer className={className}>
      <StDocumentFilter
        selected={selectedFilter}
        fileTypes={filters}
        onSelected={(fileTypeId: string) => {
          onFilterChange(fileTypeId as PatientDocumentsFilterOption)
        }}
      />
      <StListContainer>
        <DocumentListContent
          incomingFilesByYear={incomingFilesByYear}
          isLoading={isLoading}
          selectedDocumentId={selectedDocumentId}
          selectedFilter={selectedFilter}
          onDocumentClick={onDocumentClick}
        />
      </StListContainer>
    </StContainer>
  )
}

const DocumentListContent = ({
  incomingFilesByYear,
  isLoading,
  selectedDocumentId,
  selectedFilter,
  onDocumentClick,
}: {
  incomingFilesByYear: PatientViewDocumentsByYear
  isLoading?: boolean
  selectedDocumentId?: string
  selectedFilter: PatientDocumentsFilterOption
  onDocumentClick: (documentId: string) => void
}) => {
  const theme = useTheme()
  const screenType = useScreenType()

  if (isLoading) {
    return <Loader />
  }

  const years = Object.keys(incomingFilesByYear)

  if (years.length === 0) {
    return (
      <Padding px={theme.UI.Spacing.Space8}>
        <DocumentsEmptySection typeCopy={selectedFilter} />
      </Padding>
    )
  }

  return (
    <LinksByYear>
      {Object.entries(incomingFilesByYear)
        .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
        .map(([year, files]) => {
          // Keep the incoming order stable to prevent items from jumping around
          // when their unread/read status changes optimistically.
          const orderedFiles = files

          return (
            <div key={year}>
              <LinkHeader noMargin>{year}</LinkHeader>
              <LinkList>
                {orderedFiles.map((file, index, allFiles) => {
                  const id = file.id

                  const showDivider =
                    screenType === ScreenType.Desktop && showDividerBelow(file.status, index, allFiles)

                  return (
                    <Fragment key={id}>
                      <DocumentLink
                        key={id}
                        document={file}
                        selected={selectedDocumentId === id.toString()}
                        onClick={() => onDocumentClick(file.id)}
                      />
                      {showDivider && (
                        <Spacing
                          height={1}
                          style={{
                            backgroundColor: theme.Colors.Neutrals.Lighter,
                          }}
                        />
                      )}
                    </Fragment>
                  )
                })}
              </LinkList>
            </div>
          )
        })}
    </LinksByYear>
  )
}

const StContainer = styled(FlexColumn)`
  flex: 1;
  padding-bottom: 0;
  height: 100%;
`

const StDocumentFilter = styled(DocumentFilter)`
  padding: ${({ theme }) => theme.UI.SpacingPx.Space6};

  @media ${breakpoints.m} {
    padding: 0;
    padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space2};
  }
`

const StListContainer = styled.div`
  flex: 1;
  flex-direction: row;
  padding-bottom: 0;
`

export const LinkList = styled.div`
  display: flex;
  flex-direction: column;
`

export const LinkHeader = styled(H3)`
  width: 100%;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space2}
    ${({ theme }) => theme.UI.SpacingPx.Space6};
`

export const LinksByYear = styled.div`
  overflow-y: auto;

  @media ${breakpoints.fromS} {
    margin: 0 -${({ theme }) => theme.UI.SpacingPx.Space6};
    padding: 0 ${({ theme }) => theme.UI.SpacingPx.Space6};
  }
`

const showDividerBelow = (status: string | undefined, index: number, allFiles: PatientViewDocument[]) => {
  // Don't show divider after the last item in a year group
  return !(index === allFiles.length - 1)
}
